import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import { FC } from 'react';
import { Refund } from 'data/e-commerce/refunds';
import RefundStatusBadge from 'components/refunds/RefundStatusBadge';
import RmaResolutionBadge from 'components/refunds/RmaResolutionBadge';
import { useTranslation } from 'react-i18next';
import useAdvanceTable from 'hooks/products/useAdvanceTable';

// Create a type that includes all properties from ColumnDef<Refund> plus visible property
export type RefundColumnDef = ColumnDef<Refund, any> & {
  visible?: boolean;
  accessorKey?: string;
  id?: string;
}

// Create column function with translation capability
export const createRefundsTableColumns = (t: (key: string) => string): RefundColumnDef[] => [
  {
    accessorKey: 'id',
    header: t('refundsTable.headers.rmaId'),
    visible: true,
    cell: ({ row: { original } }) => {
      const { id, expedition_number } = original;
      const displayId = expedition_number || id;
      return (
        <Link to={`/refunds/${id}`} className="fw-semibold">
          #{displayId}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4' }
    }
  },
  {
    accessorKey: 'order_ref',
    header: t('refundsTable.headers.orderReference'),
    visible: true,
    cell: ({ row: { original } }) => {
      // Use order_id for the direct link to the order
      const path = original.order_id ? `/orders/${original.order_id}` : `/orders?search=${original.order_ref}`;
      return (
        <Link to={path} className="fw-semibold">
          #{original.order_ref}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '15%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4 text-body fw-semibold' }
    }
  },
  {
    accessorKey: 'customer_name',
    header: t('refundsTable.headers.customer'),
    visible: true,
    cell: ({ row: { original } }) => {
      return (
        <p className="mb-0 text-body-emphasis fw-bold">{original.customer_name}</p>
      );
    },
    meta: {
      headerProps: { style: { width: '18%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4 py-2 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'resolution_type',
    header: t('refundsTable.headers.resolution'),
    visible: true,
    cell: ({ row: { original } }) => {
      // If resolution_type is a number, use it directly; otherwise, convert from string
      let resolutionTypeNum = 0;
      
      if (original.resolution_type !== undefined) {
        if (typeof original.resolution_type === 'number') {
          resolutionTypeNum = original.resolution_type;
        } else if (typeof original.resolution_type === 'string') {
          // Try to parse the string to a number
          const parsed = parseInt(original.resolution_type, 10);
          if (!isNaN(parsed)) {
            resolutionTypeNum = parsed;
          }
        }
      }
      
      // Only render badge if there's a valid resolution type
      return resolutionTypeNum > 0 ? (
        <div className="d-flex align-items-center">
          <RmaResolutionBadge resolutionType={resolutionTypeNum} />
        </div>
      ) : null;
    },
    meta: {
      headerProps: { style: { width: '12%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4' }
    }
  },
  {
    accessorKey: 'refunded_amount',
    header: t('refundsTable.headers.refundedAmount'),
    visible: true,
    cell: ({ row: { original } }) => {
      // Format the amount as currency if it exists
      if (original.refunded_amount === undefined || original.refunded_amount === null) {
        return '';
      }
      
      // Format as currency with 2 decimal places
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TND', // You might want to make this configurable based on store settings
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return (
        <p className="mb-0 text-body fw-semibold">
          {formatter.format(original.refunded_amount)}
        </p>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4 text-body' }
    }
  },
  {
    id: 'status',
    header: t('refundsTable.headers.status'),
    visible: true,
    cell: ({ row: { original } }) => {
      // Safely access the status code
      const statusCode = original?.status?.code ?? 0; // Default to 0 (Pending) if no status
      
      // Use the RefundStatusBadge component to display status
      return (
        <div className="d-flex align-items-center">
          <RefundStatusBadge status={statusCode} />
        </div>
      );
    },
    // Use accessorFn for sorting
    accessorFn: (row: Refund) => row.status?.code ?? 0,
    meta: {
      headerProps: { style: { width: '10%' }, className: 'ps-4' },
      cellProps: { className: 'ps-4' }
    }
  },
  {
    accessorKey: 'created_at',
    header: t('refundsTable.headers.date'),
    visible: true,
    cell: ({ row: { original } }) => {
      // Format the date similar to Orders table
      if (!original.created_at) return '';
      
      try {
        const date = new Date(original.created_at);
        
        // Get month abbreviation
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        
        // Get day and year
        const day = date.getDate();
        const year = date.getFullYear();
        
        // Get time in 12-hour format with AM/PM
        let hours = date.getHours();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const minutes = date.getMinutes().toString().padStart(2, '0');
        
        return <span>{`${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`}</span>;
      } catch (e) {
        console.error('Error formatting date:', e);
        return <span>{original.created_at}</span>; // Return original string if formatting fails
      }
    },
    meta: {
      headerProps: { className: 'ps-4' },
      cellProps: {
        className: 'ps-4 text-body-tertiary fs-9 white-space-nowrap'
      }
    }
  }
];

// Export the columns for backward compatibility
// This default export uses translation keys directly as fallback
export const refundsTableColumns = createRefundsTableColumns(key => key);

interface RefundsTableProps {
  visibleColumns?: Record<string, boolean>;
  onToggleColumn?: (columnId: string, visible: boolean) => void;
  totalCount?: number;
}

const RefundsTable: FC<RefundsTableProps> = ({ totalCount }) => {
  // Use translation hook
  const { t } = useTranslation();
  
  // Create translated columns
  const translatedColumns = createRefundsTableColumns(t);

  // The parent Refunds component provides the data via AdvanceTableProvider
  // We just need to render the AdvanceTable and AdvanceTableFooter without columns
  return (
    <div>
      <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
      <AdvanceTableFooter 
        pagination 
        totalCount={totalCount} 
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default RefundsTable;
